import { setRoute } from 'src/modules/routes/actions/set';
import { parse as parseRoute } from 'src/utils/route';
import { ReduxRawState } from './Redux/utils/ReduxRawState';
import { ReduxRouterState, RouterParametersType, RouterValueType } from './Redux/ReduxRouterState';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { autorun } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import {
    BonusCreationRouteType,
    BonusCreationViewSchema,
    BonusCreationViewType,
    CampaignRouteType,
    CampaignViewSchema,
    CampaignViewType,
    CollectionType,
} from 'src/domains/casino/models/models';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { ResponsibleGamblingTabItemsRoute } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs';
import { PromotionHistoryTabType } from 'src/components/CustomersBar/CustomerTab/PromotionHistory/PromotionHistoryTabs/PromotionHistoryTabs';

export class RouterBackoffice {
    private readonly reduxStore: ReduxRawState;
    private readonly routerReduxState: ReduxRouterState;

    public constructor(
        reduxStore: ReduxRawState,
        public readonly websocketV2: ModelsState
    ) {
        this.reduxStore = reduxStore;
        this.routerReduxState = new ReduxRouterState(reduxStore);
    }

    public redirectToNamespace = (path: string, params: Record<string, string | number | null>): void => {
        this.routerReduxState.setPrevData();

        const { name, value } = parseRoute(path);

        if (name === 'global' && !Object.prototype.hasOwnProperty.call(params, '*audits')) {
            const currentGlobalValue = this.routerReduxState.getValueNamespace('global');

            if (currentGlobalValue !== null && currentGlobalValue !== value) {
                this.reduxStore.dispatch(setRoute('audits', {}));
            }
        }

        this.reduxStore.dispatch(setRoute(path, params));
    };

    public redirectToNamespaceEventMarket = (eventId: number, marketId: MarketId): void => {
        this.redirectToNamespace(`events:${eventId}`, {
            tab: 'markets',
            market: marketId.toOldId(),
            filterMarket: 'open',
        });
    };

    public closeEventMarketByFilter = (eventId: number, filterMarket: string): void => {
        this.redirectToNamespace(`events:${eventId}`, {
            tab: 'markets',
            filterMarket: filterMarket,
        });
    };

    public redirectToTelebetSearchEvent = (userId: number, eventId: number): void => {
        autorun((dispose) => {
            const eventModel = this.websocketV2.getEvent(eventId);

            if (eventModel === null) {
                return;
            }

            dispose.dispose();

            const { competition, sport, timeSettingsStartTime } = eventModel;

            if (sport === 'horseracing' || sport === 'greyhoundracing') {
                const dayName = DateTime.from(timeSettingsStartTime)?.format('dddd');

                this.redirectToNamespace(`customers:${userId}`, {
                    competition,
                    event: eventId,
                    search: 'on',
                    sport,
                    tab: 'telebet',
                    'time-filter': dayName ?? 'error',
                });
            } else {
                this.redirectToNamespace(`customers:${userId}`, {
                    competition,
                    event: eventId,
                    search: 'on',
                    sport,
                    tab: 'telebet',
                });
            }
        });
    };

    public redirectToEventAudit = (idNew: EventId): void => {
        const id = idNew.toOldId();

        this.redirectToNamespace('global:trading', {
            '*events': id,
            '*events.parameters': "{'tab': 'event-settings'}",
            '*global:customers': `trading_content_event-change-${id}`,
            '*global:audits': `trading_content_event-change-${id}`,
            '*audits': `trading_content_event-change-${id}`,
        });
    };

    public redirectToGlobal = (): void => {
        this.redirectToNamespace('global:trading', {});
    };

    public redirectToTelebet = (): void => {
        this.setParamInNamespace('customers', 'tab', 'telebet');
    };

    public redirectToSpecificUserBetslip = (userId: number): void => {
        this.redirectToNamespace(`customers:${userId}`, { betslip: 'betslip' });
    };

    public redirectToSpecificUserChat = (chatId: string): void => {
        this.redirectToNamespace(`customers:${chatId}`, {
            'tab.tab': 'customer-summary',
            chat: 'chat',
        });
    };

    public redirectToCustomer = (): void => {
        this.setParamInNamespace('customers', 'tab', 'customer');
    };

    public redirectToCustomerSummary = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'customer-summary');
    };

    public redirectToCustomerBanking = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'banking');
    };

    public redirectToCustomerBankingDeposit = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'deposit');
    };

    public redirectToCustomerBankingWithdrawal = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'withdrawal');
    };

    public redirectToCustomerBankingManualAdjustment = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'manual-adjustment');
    };

    public redirectToCustomerBankingFreebetProcedure = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'freebet-procedure');
    };

    public redirectToCustomerPersonalDetails = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'personal-details');
    };

    public redirectToCustomerCustomerSettings = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'customer-settings');
    };
    public redirectToCustomerCustomerSettingsNew = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'customer-settings-new');
    };
    public redirectToCustomerPromotionHistory = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'customer-promotion-history');
    };

    public redirectToCustomerResponsibleGambling = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'responsible-gambling');
    };

    public redirectToCustomerResponsibleGamblingDepositLimit = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.DEPOSITLIMITS);
    };

    public redirectToCustomerResponsibleGamblingNetDepositLimit = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.NETDEPOSITLIMITS);
    };

    public redirectToCustomerResponsibleGamblingSelfExclusion = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.SELFEXCLUSION);
    };

    public redirectToCustomerResponsibleGamblingPlayBreak = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.PLAYBRAKE);
    };

    public redirectToCustomerResponsibleGamblingRealityCheck = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.REALITYCHECK);
    };

    public redirectToCustomerResponsibleGamblingSrgQuestions = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.SRGQUESTIONS);
    };

    public redirectToCustomerResponsibleGamblingComplianceRestriction = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.COMPLIANCERESTRICTION);
    };

    public redirectToCustomerResponsibleGamblingDepositRestriction = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.DEPOSITRESTRICTION);
    };

    public redirectToCustomerMarkerOfHarm = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.MARKERSOFHARM);
    };

    public redirectToCustomerGamprotect = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.GAMPROTECT);
    };

    public redirectToCustomerGamstop = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.GAMSTOP);
    };

    public redirectToCustomerResponsibleGamblingAccountConfig = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', ResponsibleGamblingTabItemsRoute.ACCOUNTCONFIG);
    };

    public redirectToCustomerTransactionHistory = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'transaction-history');
    };

    public redirectToCustomerFreeSpinTransactionHistory = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'free-spin-transaction-history');
    };

    public redirectToTransactionHistoryAll = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'all');
    };

    public redirectToTransactionHistoryIncoming = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'into');
    };

    public redirectToTransactionHistoryOutgoing = (): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', 'out');
    };

    public redirectToCustomerMarketing = (): void => {
        this.setParamInNamespace('customers', 'tab.tab', 'marketing');
    };

    public redirectToCustomerPromotionHistoryTab = (tabId: PromotionHistoryTabType): void => {
        this.setParamInNamespace('customers', 'tab.tab.tab', tabId);
    };

    /* Trading tab */
    public redirectToTradingEvents = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'events',
        });
    };

    public redirectToTradingLiabilities = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'liabilities',
        });
    };

    public redirectToTradingTags = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'tags',
        });
    };

    public redirectToTradingBetSearch = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'bet-search',
        });
    };

    public redirectToTradingPaymentSearch = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'payment-search',
        });
    };

    public redirectToTradingPaymentSearchV2 = (): void => {
        this.redirectToNamespace('global:tradingV2', {
            tab: 'payment-searchV2',
        });
    };

    public redirectToTradingCsvUpload = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'csv-upload',
        });
    };

    public redirectToTradingCsvUploadV2 = (): void => {
        this.redirectToNamespace('global:tradingV2', {
            tab: 'csv-uploadV2',
        });
    };

    public redirectToTradingAutomation = (): void => {
        this.redirectToNamespace('global:trading', {
            tab: 'automation',
        });
    };

    public redirectToBetTickerV2 = (): void => {
        this.redirectToNamespace('global:betTickerV2', {});
    };

    /* Event details */
    public redirectToTradingEvent = (eventId: number): void => {
        this.redirectToNamespace(`events:${eventId}`, {});
    };
    public redirectToTradingEventDetailsSettings = (): void => {
        this.setParamInNamespace('events', 'tab', 'event-settings');
    };

    public redirectToTradingEventDetailsMarkets = (): void => {
        this.setParamInNamespace('events', 'tab', 'markets');
    };

    public redirectToTradingEventDetailsTags = (): void => {
        this.setParamInNamespace('events', 'tab', 'tags');
    };

    public redirectToTradingEventDetailsResults = (): void => {
        this.setParamInNamespace('events', 'tab', 'results');
    };

    public redirectToTradingEventSettings = (): void => {
        this.setParamInNamespace('events', 'tab', 'debug');
    };
    /* end of Event details */

    /* Admin tab */
    public redirectToAdminUser = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'users',
        });
    };

    public redirectToAdminRoleAndPermission = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'role-and-permissions',
        });
    };

    public redirectToAdminCountryDefaults = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'country-defaults',
        });
    };

    public redirectToAdminChangePassword = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'change-password',
        });
    };

    public redirectToAdminSystemSettings = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'system-settings',
        });
    };

    /* System Settings submenu */
    public redirectToBogSettings = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'system-settings',
            'tab.tab': 'bog-settings',
        });
    };

    public redirectToBetLimitSettings = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'system-settings',
            'tab.tab': 'bet-limit-settings',
        });
    };

    public redirectToWidgetConfigs = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'system-settings',
            'tab.tab': 'widget-settings',
        });
    };

    public redirectToCashOutSettings = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'system-settings',
            'tab.tab': 'cash-out-settings',
        });
    };

    public redirectToBetReferralFeature = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'system-settings',
            'tab.tab': 'bet-referral-feature',
        });
    };
    /* eof System Settings submenu */

    public redirectToAdminPricingLadders = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'pricing-ladders',
        });
    };

    public redirectToAdminRelatedContingency = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'related-contingency',
        });
    };

    public redirectToAdminChatNightMode = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'chat-night-mode',
        });
    };

    public redirectToAdminCasinoMobileVersion = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'casino-mobile-version',
        });
    };

    public redirectToAdminAppUpdate = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'app-update',
        });
    };

    public redirectToAdminConfigurationManager = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'configuration-manager',
        });
    };

    public redirectToAdminLicenseConfig = (): void => {
        this.redirectToComplianceConfigurationLicenseConfig();
    };

    public redirectToAdminComplianceConfiguration = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'gamstop-previously-positive',
        });
    };

    public redirectToComplianceConfigurationGamstopPreviouslyPositive = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'gamstop-previously-positive',
        });
    };

    public redirectToComplianceConfigurationSourceOfFunds = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'source-of-funds',
        });
    };

    public redirectToComplianceConfigurationSelfExclusion = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'self-exclusion',
        });
    };

    public redirectToComplianceConfigurationLicenseConfig = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'license-config',
        });
    };

    public redirectToComplianceConfigurationAutoLogout = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'auto-logout',
        });
    };

    public redirectToComplianceConfigurationActivityTimeframe = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'activity-timeframe',
        });
    };

    public redirectToComplianceConfigurationDefaultDepositLimits = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'default-deposit-limits',
        });
    };

    public redirectToComplianceConfigurationMarkersOfHarmPopup = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'markers-of-harm-popup',
        });
    };

    public redirectToLicenseConfigGlobal = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'license-config',
        });
    };

    public redirectToLicenseConfigAccount = (): void => {
        this.redirectToNamespace('global:admin', {
            tab: 'compliance-configuration',
            'tab.tab': 'license-config',
        });
    };

    /* Fraud and compliance */
    public redirectToManualWithdrawals = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'manual-withdrawals',
        });
    };

    public redirectToAutomaticWithdrawals = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'automatic-withdrawals',
        });
    };

    public redirectToAffordabilityExcluded = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'affordability-excluded',
        });
    };

    public redirectToPEP = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'pep',
        });
    };

    public redirectToMatchedAccount = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'matched-account',
        });
    };

    public redirectToPaymentDeclines = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'payment-declines',
        });
    };

    public redirectToMatchedPaymentMethods = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'new-matched-payment-methods',
        });
    };

    public redirectToReviewAccount = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'review-account',
        });
    };

    public redirectToCallCreditAgeVerification = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'call-credit-age-verification',
        });
    };

    public redirectToCallCreditValidationErrors = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'call-credit-validation-errors',
        });
    };

    public redirectToGamstopAccountNotLongerBlocked = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'gamstop-account-not-longer-blocked',
        });
    };

    public redirectToMohPopupTriggers = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'moh-popup-triggers',
        });
    };

    public redirectToSelfExclusionChanges = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'self-exclusion-changes',
        });
    };

    public redirectToReviewDepositRestrictions = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'deposit-restrictions',
        });
    };

    public redirectToIdMissionVerifications = (): void => {
        this.redirectToNamespace('global:fraud-and-compliance', {
            tab: 'id-mission-verifications',
        });
    };

    public redirectToMarkersOfHarmPopup = (): void => {
        this.redirectToComplianceConfigurationMarkersOfHarmPopup();
    };
    /* eof Fraud and compliance */

    /* Casino CMS */
    public redirectToCasinoCollection = (collection: CollectionType): void => {
        this.redirectToNamespace('global:casino', {
            'casino-tab': collection,
            'casino-view': 'games',
        });
    };

    public redirectToEditGame = (id: number, collection: CollectionType): void => {
        this.redirectToNamespace('global:casino', {
            'casino-tab': collection,
            'casino-view': 'edit-live-game',
            id: id,
        });
    };
    /* eof Casino CMS */

    /* Bonus Engine CMS */

    public redirectToBonusCampaignList = (): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'campaign-list',
        });
    };

    public redirectToBonusCalendar = (): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'calendar',
        });
    };

    public redirectToBonusAnalytics = (): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'analytics',
        });
    };

    public redirectToBonusCreator = (): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'bonus-creator',
        });
    };

    public redirectToBonusNewCampaign = (view: CampaignViewType, cloneId: string | null): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'new-campaign',
            'bonus-view': view,
            cloneId,
        });
    };

    public redirectToBonusEditCampaign = (view: CampaignViewType, campaignId: string): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'edit-campaign',
            'bonus-view': view,
            campaignId,
        });
    };

    public redirectToNewBonusCreation = (view: BonusCreationViewType, cloneId: string | null): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'new-bonus-creation',
            'bonus-view': view,
            cloneId,
        });
    };

    public redirectToEditBonusCreation = (view: BonusCreationViewType, bonusCreationId: string): void => {
        this.redirectToNamespace('global:bonus-engine', {
            'bonus-tab': 'edit-bonus-creation',
            'bonus-view': view,
            bonusCreationId,
        });
    };

    public redirectToCampaign = (campaignRoute: CampaignRouteType): void => {
        const { type, view } = campaignRoute;
        if (type === 'NEW') {
            this.redirectToBonusNewCampaign(view, campaignRoute.cloneId);
        }
        if (type === 'EDIT') {
            this.redirectToBonusEditCampaign(view, campaignRoute.campaignId);
        }
    };

    public redirectToBonusCreation = (bonusCreationRoute: BonusCreationRouteType): void => {
        const { type, view } = bonusCreationRoute;
        if (type === 'NEW') {
            this.redirectToNewBonusCreation(view, bonusCreationRoute.cloneId);
        }
        if (type === 'EDIT') {
            this.redirectToEditBonusCreation(view, bonusCreationRoute.bonusCreationId);
        }
    };

    public get bonusTabParam(): string | null {
        return this.getParametersString('global', 'bonus-tab');
    }

    public get bonusCreationParams(): BonusCreationRouteType | null {
        const bonusTabType = this.bonusTabParam;
        const view = this.getParametersString('global', 'bonus-view');
        const bonusCreationId = this.getParametersString('global', 'bonusCreationId');
        const cloneId = this.getParametersString('global', 'cloneId');
        const parsedViewType = BonusCreationViewSchema.safeParse(view);
        if (bonusTabType === 'new-bonus-creation') {
            if (parsedViewType.success) {
                return {
                    type: 'NEW',
                    view: parsedViewType.data,
                    cloneId,
                };
            }
            console.error('Bonus Reward params are invalid');
        }

        if (bonusTabType === 'edit-bonus-creation') {
            if (parsedViewType.success) {
                if (bonusCreationId === null) {
                    console.error('bonusCreationId is null');
                }
                return {
                    type: 'EDIT',
                    view: parsedViewType.data,
                    bonusCreationId: bonusCreationId ?? '',
                };
            }
        }

        console.error('Bonus Reward router params are invalid');
        return null;
    }

    public get bonusCampaignParams(): CampaignRouteType | null {
        const type = this.getParametersString('global', 'bonus-tab');
        const view = this.getParametersString('global', 'bonus-view');
        const campaignId = this.getParametersString('global', 'campaignId');
        const cloneId = this.getParametersString('global', 'cloneId');
        const parsedViewType = CampaignViewSchema.safeParse(view);
        if (type === 'new-campaign') {
            if (parsedViewType.success) {
                return {
                    type: 'NEW',
                    view: parsedViewType.data,
                    cloneId,
                };
            }
            console.error('Bonus campaign params are invalid');
        }

        if (type === 'edit-campaign') {
            if (parsedViewType.success) {
                if (campaignId === null) {
                    console.error('campaignId is null');
                }
                return {
                    type: 'EDIT',
                    view: parsedViewType.data,
                    campaignId: campaignId ?? '',
                };
            }
        }

        console.error('Bonus campaign router params are invalid');
        return null;
    }

    public get bonusCampaignIdParam(): string | null {
        return this.getParametersString('global', 'campaignId');
    }

    public get bonusCreationIdParam(): string | null {
        return this.getParametersString('global', 'bonusCreationId');
    }

    public get bonusCampaignCloneIdParam(): string | null {
        return this.getParametersString('global', 'cloneId');
    }

    public get bonusViewParam(): CampaignViewType | null {
        const param = this.getParametersString('global', 'bonus-view');
        const parsedParam = CampaignViewSchema.safeParse(param);

        if (parsedParam.success) {
            return parsedParam.data;
        }

        return null;
    }

    public get bonusRewardViewParam(): BonusCreationViewType | null {
        const param = this.getParametersString('global', 'bonus-view');
        const parsedParam = BonusCreationViewSchema.safeParse(param);

        if (parsedParam.success) {
            return parsedParam.data;
        }

        return null;
    }

    /* eof Bonus Engine CMS */

    /* CMS */
    public redirectToCmsCasinoBanners = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'casinoBanners',
        });
    };

    public redirectToHeroEventDetails = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'eventDetails',
        });
    };

    public redirectToCmsFooterManagement = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'footerManagement',
        });
    };

    public redirectToCmsGlobal = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'global',
        });
    };

    public redirectToCmsGlobalV2 = (): void => {
        this.redirectToNamespace('global:cmsV2', {
            tab: 'globalV2',
        });
    };

    public redirectToCmsHeaderLinkPanel = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'headerLinkPanel',
        });
    };

    public redirectToCmsHeadlineCarousel = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'headlineCarousel',
        });
    };

    public redirectToCmsHeroWidget = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'heroWidget',
        });
    };

    public redirectToCmsHomePageCarousel = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'homePageCarousel',
        });
    };

    public redirectToCmsLandingPage = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'landingPage',
        });
    };

    public redirectToCmsLiveNotificationsPage = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'liveNotificationsPage',
        });
    };

    public redirectToCmsNotificationPage = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'notificationPage',
        });
    };

    public redirectToCmsPages = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'pages',
        });
    };

    public redirectToCmsPagesV2 = (): void => {
        this.redirectToNamespace('global:cmsV2', {
            tab: 'pagesV2',
        });
    };

    public redirectToCmsPromoCode = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'promoCode',
        });
    };

    public redirectToCmsQuickLinkTray = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'quickLinkTray',
        });
    };

    public redirectToCmsPromotedEvents = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'promotedEvents',
        });
    };

    public redirectToCmsTopEvents = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'topEvents',
        });
    };

    public redirectToCmsSpecialSports = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'specialSports',
        });
    };

    public redirectToCmsSports = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'sports',
        });
    };

    public redirectToCmsTranslations = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'translations',
        });
    };

    public redirectToCmsWelcomeBonus = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'welcomeBonus',
        });
    };

    public redirectToCmsSeoManagement = (): void => {
        this.redirectToNamespace('global:cms', {
            tab: 'seoManagement',
        });
    };

    /* eof CMS */

    public getParametersString(namespace: string, name: string): string | null {
        return this.routerReduxState.getParametersString(namespace, name);
    }

    public getParametersNumber(namespace: string, name: string): number | null {
        return this.routerReduxState.getParametersNumber(namespace, name);
    }

    public getValueString(namespace: string): RouterValueType | null {
        return this.routerReduxState.getValueNamespace(namespace);
    }

    public getPrevValueString(namespace: string): RouterValueType | null {
        return this.routerReduxState.getPrevValueNamespace(namespace);
    }

    public getParametersAllFromNamespace(namespace: string): RouterParametersType {
        return this.routerReduxState.getParametersAll(namespace);
    }

    public removeParamInNamespace(namespace: string, name: string): void {
        const allParameters = this.routerReduxState.getParametersAll(namespace);
        delete allParameters[name];
        const namespaceValue = this.routerReduxState.getValueNamespace(namespace);
        if (namespaceValue === null) {
            console.error('removeParamInNamespace -> Error in redirect', namespace, name);
            return;
        }

        const namespaceTo = `${namespace}:${namespaceValue}`;

        this.redirectToNamespace(namespaceTo, allParameters);
    }

    public setParamInNamespace(namespace: string, name: string, value: RouterValueType): void {
        const allParameters = this.routerReduxState.getParametersAll(namespace);

        allParameters[name] = value;

        const namespaceValue = this.routerReduxState.getValueNamespace(namespace);

        if (namespaceValue === null) {
            console.error('setParamInNamespace -> Error in redirect', namespace, name);
            return;
        }

        const namespaceTo = `${namespace}:${namespaceValue}`;

        this.redirectToNamespace(namespaceTo, allParameters);
    }

    public getParameter(namespace: string, name: string): string | null {
        const allParameters = this.routerReduxState.getParametersAll(namespace);
        const parameterValue = allParameters[name];

        return parameterValue?.toString() ?? null;
    }

    public setParamInNamespaceNumber(namespace: string, name: string, value: number): void {
        this.setParamInNamespace(namespace, name, value);
    }

    public closeEventPagePopup = (): void => {
        this.redirectToNamespace('events', {});
    };

    public onOpenEvent = (eventId: number): void => {
        const eventsList = this.getParametersString('memo', 'opened');
        let eventsUrl = eventId.toString();
        if (eventsList !== null) {
            eventsUrl = `${eventsList}&${eventId}`;
        }
        this.redirectToNamespace('global:memo', { events: eventsUrl });
    };
}
